import { Injectable, signal, type Signal, type WritableSignal } from '@angular/core';
import { Organization } from '../models/entities';

class OrgChangeEvent extends Organization {
  constructor(org: Partial<Organization>) {
    super(org);    
  }
}

class OrgDropdownChangeEvent extends OrgChangeEvent {
  static readonly type = 'orgDropdownChange' as const;
  
  constructor(org: Partial<Organization>) {
    super(org);    
  }
}

class OrgSelectionChangeEvent extends OrgChangeEvent {
  static readonly type = 'orgSelectionChange' as const;
}

class WhiteLabelChangeEvent extends OrgChangeEvent {
  static readonly type = 'whiteLabelChange' as const;
}

export class Events {
  static readonly OrgDropdownChange = OrgDropdownChangeEvent;
  static readonly OrgSelectionChange = OrgSelectionChangeEvent;
  static readonly WhiteLabelChange = WhiteLabelChangeEvent;
}

type EventType = 
  | typeof Events.OrgDropdownChange.type
  | typeof Events.OrgSelectionChange.type
  | typeof Events.WhiteLabelChange.type;

type EventPayload<T extends EventType> = 
  T extends typeof Events.OrgDropdownChange.type ? InstanceType<typeof Events.OrgDropdownChange> :
  T extends typeof Events.OrgSelectionChange.type ? InstanceType<typeof Events.OrgSelectionChange> :
  T extends typeof Events.WhiteLabelChange.type ? InstanceType<typeof Events.WhiteLabelChange> :
  never;

@Injectable({
  providedIn: 'root'
})
export class EventService {
  private readonly events = new Map<EventType, WritableSignal<any>>();

  constructor() {
    this.events.set(Events.OrgDropdownChange.type, signal(null));    
    this.events.set(Events.OrgSelectionChange.type, signal(null));
    this.events.set(Events.WhiteLabelChange.type, signal(null));
  }

  getEvent<T extends EventType>(type: T): Signal<EventPayload<T>> {
    if (!this.events.has(type)) {
      this.events.set(type, signal(null));
    }
    return this.events.get(type)!;
  }

  emit<T extends EventType>(type: T, payload: EventPayload<T>): void {
    if (payload === null || payload === undefined) {
      return;
    }

    if (!this.events.has(type)) {
      this.events.set(type, signal(payload));
    } else {
      const signalRef = this.events.get(type)! as WritableSignal<EventPayload<T>>;
      
      let newPayload: EventPayload<T>;
      if (type === Events.OrgDropdownChange.type) {
        const payloadCopy = JSON.parse(JSON.stringify(payload));
        newPayload = new Events.OrgDropdownChange(payloadCopy) as EventPayload<T>;        
      } else if (type === Events.OrgSelectionChange.type) {
        newPayload = new Events.OrgSelectionChange({ ...(payload as any) }) as EventPayload<T>;
      } else if (type === Events.WhiteLabelChange.type) {
        newPayload = new Events.WhiteLabelChange({ ...(payload as any) }) as EventPayload<T>;
      } else {
        newPayload = { ...payload } as EventPayload<T>;
      }
      
      signalRef.set(newPayload);
    }
  }
}
